import { Component, EventEmitter, Input, Output } from '@angular/core';
import { HistoryServicesService } from 'src/app/services/history-services/history-services.service';
import { TranslateService } from '@ngx-translate/core';
import { formatDateISO, formatHourISO, parseGrpcLocal } from 'src/app/utils';
import { finalize } from 'rxjs';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TuiAlertService } from '@taiga-ui/core';
import { UnassignDriverModalData } from './models/unassign-driver-model';

@Component({
  selector: 'app-unassign-driver-modal',
  templateUrl: './unassign-driver-modal.component.html',
  styleUrls: ['./unassign-driver-modal.component.css'],
})
export class UnassignDriverModalComponent {
  @Input({ required: true }) data!: UnassignDriverModalData;
  @Output() unassigned = new EventEmitter<void>();

  loading: boolean = false;

  constructor(
    private readonly svc: HistoryServicesService,
    private readonly translate: TranslateService,
    private readonly alerts: TuiAlertService,
    private readonly modal: NgbActiveModal
  ) {}

  get modalTitle(): string {
    return this.translate.instant(
      this.data.mode === 'sweeper'
        ? 'detail_service.modal_title_unassign_sweeper'
        : 'detail_service.modal_title_unassign_car'
    );
  }

  get messageHtml(): string {
    const name =
      this.data.driverName?.trim() ||
      this.translate.instant('detail_service.no_driver');
    const dtStart = parseGrpcLocal(this.data.startIso);
    const dtEnd = parseGrpcLocal(this.data.endIso);

    const datePart = dtStart
      ? this.translate.instant('detail_service.date_part', {
          date: `<strong>${formatDateISO(dtStart)}</strong>`,
        })
      : this.translate.instant('detail_service.date_unknown');

    const timePart =
      dtStart && dtEnd
        ? this.translate.instant('detail_service.time_part', {
            start: `<strong>${formatHourISO(dtStart)}</strong>`,
            end: `<strong>${formatHourISO(dtEnd)}</strong>`,
          })
        : this.translate.instant('detail_service.time_unknown');

    return this.translate.instant(
      this.data.mode === 'sweeper'
        ? 'detail_service.unassign_modal_message_html_sweeper_comp'
        : 'detail_service.unassign_modal_message_html_car',
      { name: `<strong>${name}</strong>`, datePart, timePart }
    );
  }

  onConfirm(): void {
    this.loading = true;

    const call$ =
      this.data.mode === 'sweeper'
        ? this.svc.unassignSweeper(this.data.nodeId)
        : this.svc.unassignDriver(this.data.nodeId);

    call$.pipe(finalize(() => (this.loading = false))).subscribe({
      next: (res) => {
        const ok = res.isSuccess;
        this.alert(ok);
        if (ok) {
          this.modal.close();
          this.unassigned.emit();
        }
      },
      error: () => {
        this.alert(false);
      },
    });
  }

  private alert(ok: boolean): void {
    const scope = this.data.mode === 'sweeper' ? 'sweeper' : 'car';
    const base = 'detail_service.unassign_';

    this.alerts
      .open(
        this.translate.instant(
          `${base}${ok ? 'success' : 'error'}_text_${scope}`
        ),
        {
          label: this.translate.instant(
            `${base}${ok ? 'success' : 'error'}_label_${scope}`
          ),
          status: ok ? 'success' : 'error',
          autoClose: true,
        }
      )
      .subscribe();
  }

  cancel(): void {
    this.modal.dismiss();
  }
}
